import { useEffect, useState } from 'react';
import ModalCommon from 'components/Input/ModalCommon';
import { Spin } from 'antd';
import { ModalImportWrapper } from '../styled';
import Button from 'components/Button';
import { toast } from 'sonner';
import CeuServices from 'services/CeuServices';
import { NO_INTERNET } from 'components/messageError';
import { TIME_ZONE } from 'helper/init.constants';

const ModalImportCSV = ({ onCancel, isOpen, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fileInput, setFileInput] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [errors, setErrors] = useState([]);

  const handleChooseFile = (e) => {
    setErrors([]);

    const file = e?.target?.files?.[0];
    if (!file) return;
    setFileInput(file);
    setFileName(file.name);
  };

  const handleDownload = () => {
    // Create a simple link for download
    const link = document.createElement('a');
    link.href = '/CEU_import.xlsx';
    link.download = 'CEU_import.xlsx';

    // Show toast after a delay (when the save dialog is likely closed)
    const timer = setTimeout(() => {
      toast.success('ファイルのダウンロードに成功しました');
    }, 2000); // 2 seconds delay

    // Trigger the download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Return cleanup function
    return () => clearTimeout(timer);
  };

  const handleImportCSV = () => {
    if (!fileInput) {
      setErrors(['ファイルの登録を入力してください']);
      return;
    }

    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];

    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const fileExtension = fileInput.name
      .toLowerCase()
      .substring(fileInput.name.lastIndexOf('.'));

    if (
      !validTypes.includes(fileInput.type) &&
      !validExtensions.includes(fileExtension)
    ) {
      setErrors([
        'ファイルのインポートに失敗しました。\n以下の点を確認の上、再試行してください。\n・選択ファイルに誤りがないか\n・1件以上のユーザー情報が入力されているか\n・1度にインポートできるユーザー数は1,000件までです',
      ]);
      return;
    }

    // Clear errors and create FormData
    setErrors([]);
    const formData = new FormData();
    formData.append('file', fileInput);
    formData.append('timezone', TIME_ZONE);
    setLoading(true);
    CeuServices.importCEUs(formData)
      .then((res) => {
        if (res.isOk) {
          toast.success('ファイルのインポートに成功しました');
          onCancel();
          onSuccess();
          setFileInput(null);
          setFileName(null);
          const inputEl = document.getElementById('ceu-upload-input');
          if (inputEl) inputEl.value = '';
        }
      })
      .catch((err) => {
        if (err?.message) {
          setErrors([err?.message]);
        } else if (!err?.message) setErrors([err]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (errors[0] === NO_INTERNET) {
      toast.error(NO_INTERNET);
      setErrors([]);
      return;
    }
  }, [errors.length > 0]);

  return (
    <Spin spinning={loading}>
      <ModalImportWrapper $padding={[24, 24, 0, 24]}>
        <ModalCommon
          title="一括付与"
          open={isOpen}
          heightHeader={56}
          onCancel={() => {
            onCancel();
            setLoading(false);
            setErrors([]);
            setFileInput(null);
            setFileName(null);
            const inputEl = document.getElementById('ceu-upload-input');
            if (inputEl) inputEl.value = '';
          }}
          width={600}
          footer={
            <div className="flex h-max justify-end gap-4 border-t border-gray-200 pt-[15px]">
              <Button
                btnType="back"
                width={120}
                height={40}
                onClick={() => {
                  onCancel();
                  setLoading(false);
                  setErrors([]);
                  setFileInput(null);
                  setFileName(null);
                  const inputEl = document.getElementById('ceu-upload-input');
                  if (inputEl) inputEl.value = '';
                }}
              >
                キャンセル
              </Button>
              <Button
                btnType="primary"
                width={120}
                height={40}
                loading={loading}
                onClick={handleImportCSV}
                fontSize={14}
              >
                実行する
              </Button>
            </div>
          }
        >
          <div>
            <div className="mb-8">
              <p
                className="h-4 cursor-pointer text-[14px] font-normal text-[#1890FF]"
                onClick={handleDownload}
              >
                CSVファイルのサンプルをダウンロード
              </p>
            </div>

            <div className="mb-8 flex items-center justify-between gap-2">
              <div className="w-[ text-sm text-gray-800">ファイルの登録</div>
              <div className="flex w-[416px] justify-end">
                <div className="flex h-10 w-[80%] flex-shrink-0 items-center rounded-[10px] rounded-r-none border-[1px] border-[#E9E9E9] p-3 text-[14px] text-[#00000073]">
                  {fileName}
                </div>
                <button
                  className="!rounded-tf-none !rounded-bf-none h-10 w-[88px] rounded-br-[10px] rounded-tr-[10px] bg-[#ED7836] text-[14px] font-medium text-white"
                  type="button"
                  onClick={() =>
                    document.getElementById('ceu-upload-input')?.click()
                  }
                >
                  参照する
                </button>
                <input
                  style={{
                    width: '88px',
                  }}
                  id="ceu-upload-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleChooseFile}
                />
              </div>
            </div>

            <div className="w-full">
              {errors[0] !== NO_INTERNET &&
                errors?.map((error, index) => (
                  <div
                    key={index}
                    className="mb-1 w-full whitespace-pre-wrap break-words text-sm text-red-500"
                    style={{ wordBreak: 'break-word' }}
                  >
                    {typeof error === 'string'
                      ? error
                          .split('\n')
                          .map((line, i) => <div key={i}>{line}</div>)
                      : error}
                  </div>
                ))}
            </div>
          </div>
        </ModalCommon>
      </ModalImportWrapper>
    </Spin>
  );
};

export default ModalImportCSV;
